import { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { ReportProps } from "..";
import { useAppState } from "../../../../../../../context/StateContext";
import { ChainReport, GenerateReport, ReportInfo } from "../../../../../../../models/chain.report";
import { PdfReport as ReportContent } from "../../../../../../../models/pdf.report";
import { Filters, Searches } from "../../../../../../../models/shipeezi.table";
import { ChainReportService } from "../../../../../../../services/chain.reports.service";
import { ContainerConferenceService } from "../../../../../../../services/container.conferenceservice";
import { LoadingCostService } from "../../../../../../../services/loading.cost.service";
import ProductService from "../../../../../../../services/product.service";
import ProjectService from "../../../../../../../services/project.service";
import PurchaseOrderService from "../../../../../../../services/purchase.order.service";
import ShipmentBookingService from "../../../../../../../services/shipment.booking.service";
import { ShipmentInvoiceService } from "../../../../../../../services/shipment.invoice.service";
import ShipmentService from "../../../../../../../services/shipment.service";
import { downloadAndGenerateCsv } from "Util/downloadAndGenerateCsv";
import { CronJobType, FileTypes } from "Util/enums";
import { handleGeneratePdf as handleGenerateReport } from "Util/generateReport";
import {
  BookingKeys,
  ChainReportKeys,
  ContainerConferenceKeys,
  CronjobKeys,
  LoadingCostKeys,
  PurchaseOrderKeys,
  ReportProductsKeys,
  ShipmentInvoicesKeys,
  ShipmentKeys,
} from "../../../../../../../util/queries";
import { getRelatedFilters, getTypeFromReportName } from "../utils";
import { ReportSubGroup } from "Util/enums";

const CACHE_TIME = 60 * 60 * 1000; // 1 hour

export const useActionReportMaintenance = () => {
  const { currentUser, isFMVersion } = useAppState();
  const { state } = useLocation<ReportProps>();
  const { reportGroup, name } = state || {};

  const queryClient = useQueryClient();

  const reportInfo: ReportInfo = useMemo(() => {
    return {
      reportGroup: reportGroup?.subGroup ?? "",
      reportName: name,
    };
  }, [state]);

  const type: CronJobType = useMemo(() => {
    return getTypeFromReportName(name);
  }, [name]);

  const getAllShipments = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters,
    disabled?: boolean
  ) => {
    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: !disabled && !!currentUser?.id,
      queryFn: async () => {
        const { data: rows } = await ShipmentService.getAll({
          attributes: [
            "arrivalFinalPort",
            "containerId",
            "dcDestination",
            "etaFinalPort",
            "etaUnpackDate",
            "eventType",
            "hwab",
            "id",
            "poNumbers",
            "scheduledDate",
            "shipmentType",
            "statusShipment",
          ],
          companyId: currentUser?.companyId,
          ...filters,
          include: JSON.stringify([
            {
              association: "milestones",
              include: [{ association: "milestone" }],
            },
          ]),
          isBooking: false,
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        });

        const { data: count } = await ShipmentService.count({
          companyId: currentUser?.companyId,
          ...filters,
          isBooking: false,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        });

        if (count && rows) return { count, rows };

        return { count: 0, rows: [] };
      },
      queryKey: ShipmentKeys.getAll([currentUser?.companyId, page, pageSize, searches, filters]),
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  };

  const getCrossDock = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters,
    disabled?: boolean
  ) => {
    if (filters?.date_type) {
      filters.dateType = filters.date_type;
      delete filters.date_type;
    }

    return useQuery({
      cacheTime: CACHE_TIME,
      enabled: !disabled && !!currentUser?.id,
      queryFn: async () => {
        const { data } = await ContainerConferenceService.getCrossDock({
          ...filters,
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        });

        if (data) return data;
      },
      queryKey: ContainerConferenceKeys.getCrossDock([page, pageSize, searches, filters]),
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  };

  const getAllProducts = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters,
    disabled?: boolean
  ) => {
    return useQuery({
      queryKey: ReportProductsKeys.getAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        let response: any;
        const groupBy =
          type === CronJobType.STOCK_ON_HAND
            ? JSON.stringify(["product.id", "productStocks.warehouse.id"])
            : JSON.stringify([
                "product.id",
                "cartonProducts.purchaseOrder.id",
                "cartonProducts.carton.shipments.id",
              ]);

        switch (type) {
          case CronJobType.STOCK:
            response = await ProductService.getStock(type, {
              page: page ?? 0,
              pageSize,
              companyId: currentUser?.companyId,
              searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
              ...filters,
            });
            break;
          default:
            response = await ProductService.getStockOnHand(type, {
              page: page ?? 0,
              pageSize,
              companyId: currentUser?.companyId,
              searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
              groupBy,
              ...filters,
            });
        }

        if (response && response?.data)
          return { count: response?.data?.count ?? 0, rows: response?.data?.rows ?? [] };
      },
      enabled: !disabled && !!currentUser?.id,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getAllBookings = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: BookingKeys.getAllNetwork([
        currentUser?.companyId,
        page,
        pageSize,
        searches,
        filters,
      ]),
      queryFn: async () => {
        const response = await ShipmentBookingService.getAllNetwork({
          page,
          pageSize,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          isBooking: 1,
          companyId: currentUser?.companyId,
          group: ["shipment.id"],
          attributes: [
            "id",
            "containerId",
            "origin",
            "destination",
            "shipmentType",
            "statusShipment",
            "companyId",
            "totalQuantity",
            "createdAt",
          ],
          include: JSON.stringify([
            {
              association: "purchaseOrders",
              attributes: ["reference"],
              where: getRelatedFilters(filters, "purchaseOrders"),
            },
            {
              association: "company",
              attributes: ["id"],
              include: [{ association: "supplierAssociated", attributes: ["name"] }],
            },
          ]),
          ...getRelatedFilters(filters),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const attributesPurchaseOrders = [
    "id",
    "companyId",
    "reference",
    "type",
    "productTotalQuantity",
    "date",
    "origin",
    "destination",
    "eta",
    "status",
    "performancePrediction",
    "fromIntegration",
    "unitQuantity",
    "supplierName",
    "incoterm",
    "mode",
    "addressCode",
    "createdAt",
  ];

  const includePurchaseOrders = JSON.stringify([
    { association: "milestones", include: [{ association: "milestone" }] },
    { association: "consignee", attributes: ["name"] },
  ]);

  const getAllPurchaseOrders = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: PurchaseOrderKeys.getAllNetwork([
        currentUser?.companyId,
        page,
        pageSize,
        searches,
        filters,
      ]),
      queryFn: async () => {
        const response = await PurchaseOrderService.getAllNetwork({
          page,
          pageSize,
          group: ["purchase_order.id"],
          companyId: currentUser?.companyId,
          include: includePurchaseOrders,
          attributes: attributesPurchaseOrders,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.companyId,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getBookingInvoiceList = (
    page?: number,
    pageSize?: number,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: BookingKeys.getInvoiceList([
        currentUser?.companyId,
        page,
        pageSize,
        searches,
        filters,
      ]),
      queryFn: async () => {
        const response = await ShipmentBookingService.getInvoiceList({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        });

        if (response && response.data) return response.data;
        return [];
      },
      enabled: !!currentUser?.companyId,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getAllInvoices = (
    page: number = 1,
    pageSize: number = 20,
    searches?: Searches,
    filters?: Filters
  ) => {
    return useQuery({
      queryKey: ShipmentInvoicesKeys.getAndCountAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await ShipmentInvoiceService.getAndCountAll({
          page,
          pageSize,
          groupBy: JSON.stringify(["invoices.id"]),
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          attributes: ["id", "invoiceNumber", "description"],
          include: JSON.stringify([
            {
              association: "invoiceShipment",
              attributes: [
                "id",
                "poNumber",
                "supplierName",
                "mode",
                "eta",
                "origin",
                "destination",
              ],
              where: getRelatedFilters(filters, "invoiceShipment"),
            },
          ]),
          ...getRelatedFilters(filters),
        });

        if (response && response.data) return response.data;
      },
      enabled: !!currentUser?.id,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getAllLCRData = (
    page: number = 0,
    pageSize: number = 20,
    searches?: Searches,
    filters?: Filters,
    disabled?: boolean
  ) => {
    return useQuery({
      queryKey: LoadingCostKeys.getAll([page, pageSize, searches, filters]),
      queryFn: async () => {
        const response = await LoadingCostService.getAndCountAll({
          page,
          pageSize,
          groupBy: JSON.stringify(["id"]),
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify([]),
          ...getRelatedFilters(filters),
        });

        if (response && response.data) {
          return response.data;
        }
      },
      enabled: !disabled && !!currentUser?.id,
      staleTime: 3 * 60 * 1000, // 3 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getAllProjects = () => {
    return useQuery({
      queryKey: ["projects.get.combobox"],
      queryFn: () =>
        ProjectService.getAllNetwork({
          attributes: ["id", "name", "companyId"],
          companyId: currentUser?.companyId,
          group: ["projects.id"],
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      enabled: isFMVersion,
      refetchOnWindowFocus: false,
    });
  };

  const useSendMailReport = (filters?: Filters, csvFields?: string[], include?: Object[]) => {
    return useMutation({
      mutationKey: ChainReportKeys.sendMail(""),
      mutationFn: ({ data }: { data: ChainReport }) =>
        ChainReportService.sendMail(
          {
            ...data,
            csvFields,
            type,
            companyId: currentUser?.companyId,
            ...reportInfo,
          },
          {
            companyId: currentUser?.companyId,
            ...getRelatedFilters(filters),
            include: JSON.stringify(include),
          }
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CronjobKeys.all] });
      },
    });
  };

  const useGenerateReport = () => {
    return useMutation({
      mutationKey: ChainReportKeys.generateReport(""),
      mutationFn: ({
        data,
        filters,
        include,
      }: {
        data: GenerateReport;
        filters?: Filters;
        include?: Object[];
      }) => {
        let groupBy: string | null = null;

        if (data.reportGroup && data.reportGroup === ReportSubGroup.Products) {
          groupBy =
            type === CronJobType.STOCK_ON_HAND
              ? JSON.stringify(["product.id", "productStocks.warehouse.id"])
              : JSON.stringify([
                  "product.id",
                  "cartonProducts.purchaseOrder.id",
                  "cartonProducts.carton.shipments.id",
                ]);
        }

        return ChainReportService.generateReport(data, {
          groupBy,
          companyId: currentUser?.companyId,
          ...getRelatedFilters(filters),
          include: JSON.stringify(include),
        });
      },
      onSuccess: (response) => {
        if (response?.data && response?.data?.content) {
          const { content } = response.data;

          switch (response?.data?.type) {
            case FileTypes.CSV:
              if (typeof content === "string") downloadAndGenerateCsv(content);
              break;
            case FileTypes.PDF:
              if (typeof content?.[0] === "number")
                handleGenerateReport({
                  data: response.data as ReportContent,
                  exportFile: true,
                });
              break;
            case FileTypes.XLSX:
              if (typeof content?.[0] === "number")
                handleGenerateReport({
                  data: response.data as ReportContent,
                  exportFile: true,
                });
              break;
            default:
              break;
          }
        }
      },
    });
  };

  return {
    getAllBookings,
    getAllPurchaseOrders,
    getBookingInvoiceList,
    getAllShipments,
    getAllInvoices,
    getAllProjects,
    getAllProducts,
    getAllLCRData,
    useSendMailReport,
    useGenerateReport,
    getCrossDock,
  };
};
